import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'react-hot-toast';
import { Mic, MicOff, Loader2, CheckCircle2, XCircle, Volume2, AlertCircle } from 'lucide-react';
import { getAuthHeader } from '../context/AuthContext';
import { AdminLayout } from '../components/AdminLayout';

const API = process.env.REACT_APP_BACKEND_URL || '';

function VoiceAISettingsContent() {
  const [loading, setLoading] = useState(false);
  const [loadingVoices, setLoadingVoices] = useState(true);
  const [voices, setVoices] = useState([]);
  const [status, setStatus] = useState(null);
  const [formData, setFormData] = useState({
    voice_id: '',
    greeting_message: '',
    agent_personality: 'Friendly, helpful, and professional restaurant waiter'
  });

  // Fetch current status and available voices
  useEffect(() => {
    fetchStatus();
    fetchVoices();
  }, []);

  const fetchStatus = async () => {
    try {
      const response = await axios.get(`${API}/api/voice-ai/status`, {
        headers: getAuthHeader()
      });
      setStatus(response.data);
      
      // Populate form with existing config
      if (response.data.enabled) {
        setFormData({
          voice_id: response.data.voice_id || '',
          greeting_message: response.data.greeting_message || '',
          agent_personality: 'Friendly, helpful, and professional restaurant waiter'
        });
      }
    } catch (error) {
      console.error('Error fetching voice AI status:', error);
    }
  };

  const fetchVoices = async () => {
    try {
      setLoadingVoices(true);
      const response = await axios.get(`${API}/api/voice-ai/voices`);
      console.log('Voices response:', response.data);
      setVoices(response.data.voices || []);
      
      if (!response.data.voices || response.data.voices.length === 0) {
        toast.error('No voices available from ElevenLabs');
      }
    } catch (error) {
      console.error('Error fetching voices:', error);
      toast.error(error.response?.data?.detail || 'Failed to load available voices');
    } finally {
      setLoadingVoices(false);
    }
  };

  const handleSetupAgent = async () => {
    if (!formData.voice_id) {
      toast.error('Please select a voice');
      return;
    }

    try {
      setLoading(true);
      
      const response = await axios.post(
        `${API}/api/voice-ai/setup`,
        formData,
        { headers: getAuthHeader() }
      );

      if (response.data.success) {
        toast.success('Voice AI agent created successfully!');
        fetchStatus(); // Refresh status
      }
    } catch (error) {
      console.error('Error setting up voice AI:', error);
      toast.error(error.response?.data?.detail || 'Failed to setup voice AI');
    } finally {
      setLoading(false);
    }
  };

  const handleDisable = async () => {
    try {
      setLoading(true);
      await axios.delete(`${API}/api/voice-ai/disable`, {
        headers: getAuthHeader()
      });
      toast.success('Voice AI disabled');
      fetchStatus();
    } catch (error) {
      console.error('Error disabling voice AI:', error);
      toast.error('Failed to disable voice AI');
    } finally {
      setLoading(false);
    }
  };

  const playVoicePreview = (previewUrl) => {
    if (previewUrl) {
      const audio = new Audio(previewUrl);
      audio.play();
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
          <Mic className="h-8 w-8 text-blue-600" />
          Voice AI Settings
        </h1>
        <p className="text-gray-600 mt-2">
          Configure AI voice assistant for your restaurant's online ordering
        </p>
      </div>

      {/* Status Card */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Current Status</span>
            {status?.enabled ? (
              <span className="flex items-center gap-2 text-green-600 text-sm font-normal">
                <CheckCircle2 className="h-5 w-5" />
                Active
              </span>
            ) : (
              <span className="flex items-center gap-2 text-gray-500 text-sm font-normal">
                <XCircle className="h-5 w-5" />
                Inactive
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {status?.enabled ? (
            <div className="space-y-2">
              <p className="text-sm text-gray-600">
                <strong>Agent ID:</strong> {status.agent_id}
              </p>
              <p className="text-sm text-gray-600">
                <strong>Voice ID:</strong> {status.voice_id}
              </p>
              <p className="text-sm text-gray-600">
                <strong>Last Updated:</strong> {new Date(status.last_updated).toLocaleString()}
              </p>
              {status.greeting_message && (
                <p className="text-sm text-gray-600">
                  <strong>Greeting:</strong> "{status.greeting_message}"
                </p>
              )}
              <div className="pt-4">
                <Button
                  variant="destructive"
                  onClick={handleDisable}
                  disabled={loading}
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <MicOff className="h-4 w-4" />}
                  <span className="ml-2">Disable Voice AI</span>
                </Button>
              </div>
            </div>
          ) : (
            <p className="text-gray-600">
              Voice AI is not configured. Set up your AI voice assistant below.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Configuration Card */}
      <Card>
        <CardHeader>
          <CardTitle>Configure Voice AI Agent</CardTitle>
          <CardDescription>
            Customize your restaurant's AI voice assistant
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Voice Selection */}
          <div>
            <Label htmlFor="voice">Select Voice</Label>
            
            {loadingVoices ? (
              <div className="mt-2 p-4 border rounded-lg flex items-center justify-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="text-sm text-gray-500">Loading voices from ElevenLabs...</span>
              </div>
            ) : voices.length === 0 ? (
              <div className="mt-2 p-4 border border-red-200 rounded-lg bg-red-50">
                <div className="flex items-center gap-2 text-red-600">
                  <AlertCircle className="h-4 w-4" />
                  <span className="text-sm">No voices available. Please check your ElevenLabs API key.</span>
                </div>
              </div>
            ) : (
              <Select
                value={formData.voice_id}
                onValueChange={(value) => setFormData({ ...formData, voice_id: value })}
              >
                <SelectTrigger id="voice" className="mt-2">
                  <SelectValue placeholder="Choose a voice for your AI waiter" />
                </SelectTrigger>
                <SelectContent>
                  {voices.map((voice) => (
                    <SelectItem key={voice.voice_id} value={voice.voice_id}>
                      <div className="flex items-center justify-between w-full">
                        <span>{voice.name}</span>
                        {voice.preview_url && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              playVoicePreview(voice.preview_url);
                            }}
                            className="ml-2 text-blue-600 hover:text-blue-800"
                          >
                            <Volume2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {voices.length > 0 && (
              <p className="text-xs text-gray-500 mt-1">
                {voices.length} voices available
              </p>
            )}
          </div>

          {/* Greeting Message */}
          <div>
            <Label htmlFor="greeting">Greeting Message (Optional)</Label>
            <Textarea
              id="greeting"
              placeholder="Hello! Welcome to [Your Restaurant]. May I have your name and phone number please?"
              value={formData.greeting_message}
              onChange={(e) => setFormData({ ...formData, greeting_message: e.target.value })}
              className="mt-2"
              rows={3}
            />
            <p className="text-xs text-gray-500 mt-1">
              Leave blank to use the default greeting
            </p>
          </div>

          {/* Agent Personality */}
          <div>
            <Label htmlFor="personality">Agent Personality</Label>
            <Textarea
              id="personality"
              placeholder="Friendly, helpful, and professional restaurant waiter"
              value={formData.agent_personality}
              onChange={(e) => setFormData({ ...formData, agent_personality: e.target.value })}
              className="mt-2"
              rows={2}
            />
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4 pt-4">
            <Button
              onClick={handleSetupAgent}
              disabled={loading || !formData.voice_id}
              className="flex-1"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Setting up...
                </>
              ) : (
                <>
                  <Mic className="h-4 w-4 mr-2" />
                  {status?.enabled ? 'Update Agent' : 'Create Agent'}
                </>
              )}
            </Button>
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
            <h4 className="font-semibold text-blue-900 mb-2">How it works:</h4>
            <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
              <li>Customers can click "Talk to AI Waiter" on your QR menu</li>
              <li>The AI will greet them and collect their name and phone number</li>
              <li>AI helps them browse the menu and place their order</li>
              <li>Order is automatically sent to your POS system</li>
              <li>Menu updates are automatically synced to the AI agent</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default function VoiceAISettingsPage() {
  return (
    <AdminLayout>
      <VoiceAISettingsContent />
    </AdminLayout>
  );
}
